import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import RouterIcon from '@mui/icons-material/Router';
import { toast } from 'react-toastify';

import {
  AprovisionamientoTraslado,
  ESTADO_LISTO,
  SolicitudMigracion,
  colorEstadoSerie,
  consultarMigracionTraslado,
  ejecutarMigracionTraslado,
  estadoMigracion,
  migracionTerminoBien,
  serieDe,
} from './aprovisionamiento-traslado.actions';

const ACCENT = '#1565c0';
/** Cada cuánto se pregunta cómo va la migración. */
const MS_SONDEO = 5000;

type Props = {
  ordenTrabajo: any;
  /**
   * Opción B: el flujo de activación de siempre (el botón ACTIVAR). Se llama
   * cuando la migración no se puede usar o cuando el operador decide irse por
   * ahí.
   */
  onActivarNormal: () => void;
  disabled?: boolean;
  /** Se llama UNA vez cuando la migración (ejecución) termina bien. */
  onAprovisionadoOk?: () => void;
};

/** Una fila "etiqueta: valor" para los datos de cada OLT. */
function Dato({ label, value }: { label: string; value?: string | null }) {
  return (
    <Typography variant="body2" color="text.secondary">
      {label}: <b style={{ color: 'rgba(0,0,0,.8)' }}>{value || '—'}</b>
    </Typography>
  );
}

/**
 * Aprovisionamiento del traslado (solo TRASLADOS).
 *
 * Primero consulta: el servidor resuelve la OLT donde la ONT está hoy —por el
 * NAP que reporta Softnet— y la del domicilio nuevo —la que eligió
 * Activaciones—, y dice si se puede migrar en un paso. Si se puede, el
 * operador aprovisiona desde acá y ve el avance; si no, se va por la
 * activación de siempre, que es exactamente lo que hace instalaciones.
 */
export default function AprovisionarTrasladoSection({
  ordenTrabajo,
  onActivarNormal,
  disabled = false,
  onAprovisionadoOk,
}: Props) {
  const [datos, setDatos] = useState<AprovisionamientoTraslado | null>(null);
  const [solicitud, setSolicitud] = useState<SolicitudMigracion | null>(null);
  const [consultando, setConsultando] = useState(false);
  const [ejecutando, setEjecutando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const temporizador = useRef<ReturnType<typeof setInterval> | null>(null);

  const idOrdenTrabajo: string = ordenTrabajo?.id_orden_trabajo || '';

  const detener = () => {
    if (temporizador.current) {
      clearInterval(temporizador.current);
      temporizador.current = null;
    }
  };

  // Sondeo del avance mientras la solicitud no termine. Se corta al salir de
  // la pantalla para no dejar peticiones colgando.
  useEffect(() => {
    const id = solicitud?.id_solicitud;
    if (!id || solicitud?.completada) {
      detener();
      return;
    }
    if (temporizador.current) return;
    temporizador.current = setInterval(async () => {
      try {
        const actualizado = await estadoMigracion(id);
        setSolicitud(actualizado);
        if (actualizado?.completada) detener();
      } catch {
        // Un fallo puntual del sondeo no interrumpe nada: se reintenta sola.
      }
    }, MS_SONDEO);
    return detener;
  }, [solicitud?.id_solicitud, solicitud?.completada]);

  useEffect(() => detener, []);

  const mensajeError = (e: any, porDefecto: string) =>
    e?.response?.data?.message || e?.message || porDefecto;

  const consultar = async () => {
    setError(null);
    setSolicitud(null);
    setConsultando(true);
    try {
      const res = await consultarMigracionTraslado(idOrdenTrabajo);
      setDatos(res);
      if (res.consulta) setSolicitud(res.consulta);
    } catch (e: any) {
      setError(mensajeError(e, 'No se pudo consultar el aprovisionamiento.'));
    } finally {
      setConsultando(false);
    }
  };

  const ejecutar = async () => {
    setError(null);
    avisado.current = false;
    setEjecutando(true);
    try {
      const res = await ejecutarMigracionTraslado(idOrdenTrabajo);
      setDatos(res);
      if (res.opcion === 'B') {
        toast.warn('No se pudo lanzar la migración; use la activación normal.');
      } else if (res.ejecucion) {
        setSolicitud(res.ejecucion);
        toast.info('Migración lanzada. El cliente queda sin servicio mientras corre.');
      }
    } catch (e: any) {
      setError(mensajeError(e, 'No se pudo lanzar el aprovisionamiento.'));
    } finally {
      setEjecutando(false);
    }
  };

  // Aviso de éxito (semáforo del traslado): una sola vez por ejecución.
  const avisado = useRef(false);

  const resultado = serieDe(solicitud);
  const estadoSerie = String(resultado?.estado || '').toUpperCase();
  const esConsulta = String(solicitud?.modo || '').toUpperCase() === 'CONSULTA';
  const puedeAprovisionar =
    datos?.opcion === 'A' && esConsulta && estadoSerie === ESTADO_LISTO;
  const enCurso = !!solicitud && !solicitud.completada;
  const termino = !!solicitud?.completada && !esConsulta;

  useEffect(() => {
    if (!termino || avisado.current) return;
    if (migracionTerminoBien(solicitud)) {
      avisado.current = true;
      onAprovisionadoOk?.();
    }
  }, [termino, solicitud]);

  return (
    <Grid item xs={12}>
      <Paper variant="outlined" sx={{ p: 2, borderColor: ACCENT }}>
        <Stack spacing={1.5}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <Typography variant="subtitle2" color={ACCENT}>
              Aprovisionamiento del traslado
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RouterIcon />}
              onClick={consultar}
              disabled={disabled || consultando || ejecutando || enCurso || !idOrdenTrabajo}
            >
              {datos ? 'Volver a consultar' : 'Consultar'}
            </Button>
          </Box>

          {!datos && !consultando && (
            <Typography variant="body2" color="text.secondary">
              Consulte para ver si este traslado se puede aprovisionar migrando la
              ONT de una OLT a la otra, o si va por la activación de siempre.
            </Typography>
          )}

          {consultando && (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={18} />
              <Typography variant="body2">
                Consultando OLT de origen y destino...
              </Typography>
            </Box>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {datos && (
            <>
              <Divider />
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" fontWeight={600}>
                    Donde está hoy
                  </Typography>
                  <Dato label="NAP" value={datos.actual?.nap} />
                  <Dato label="OLT" value={datos.actual?.olt} />
                  <Dato label="IPv4" value={datos.actual?.ip_olt} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" fontWeight={600}>
                    Domicilio nuevo
                  </Typography>
                  <Dato label="NAP" value={datos.nueva?.nap} />
                  <Dato label="OLT" value={datos.nueva?.olt} />
                  <Dato label="IPv4" value={datos.nueva?.ip_olt} />
                </Grid>
              </Grid>

              {datos.serie && (
                <Typography variant="body2" color="text.secondary">
                  ONT: <b>{datos.serie}</b>
                </Typography>
              )}

              {datos.opcion === 'B' && (
                <Alert severity="warning">
                  <Typography variant="body2" fontWeight={600} gutterBottom>
                    No se puede migrar en un paso:
                  </Typography>
                  {datos.motivos.map((m) => (
                    <Typography key={m} variant="body2" component="div">
                      • {m}
                    </Typography>
                  ))}
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Use la activación de siempre, igual que en instalaciones.
                  </Typography>
                </Alert>
              )}

              {resultado && (
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <Chip
                    size="small"
                    color={colorEstadoSerie(estadoSerie)}
                    label={estadoSerie || solicitud?.estado}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {resultado.mensaje || solicitud?.mensaje}
                  </Typography>
                </Box>
              )}

              {enCurso && (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={18} />
                  <Typography variant="body2">
                    {solicitud?.estado}
                    {solicitud?.fase ? ` · ${solicitud.fase}` : ''}
                  </Typography>
                </Box>
              )}

              {termino && (
                <Alert severity={migracionTerminoBien(solicitud) ? 'success' : 'warning'}>
                  {migracionTerminoBien(solicitud)
                    ? 'La ONT quedó aprovisionada en la OLT del domicilio nuevo.'
                    : 'La migración terminó sin aprovisionar. Revise el estado y, si corresponde, use la activación de siempre.'}
                </Alert>
              )}
            </>
          )}

          <Box display="flex" gap={1} flexWrap="wrap">
            {puedeAprovisionar && (
              <LoadingButton
                variant="contained"
                color="warning"
                loading={ejecutando}
                disabled={disabled || enCurso}
                onClick={ejecutar}
              >
                Aprovisionar en la OLT nueva
              </LoadingButton>
            )}
            {datos && (
              <Button
                variant={datos.opcion === 'B' ? 'contained' : 'outlined'}
                onClick={onActivarNormal}
                disabled={disabled || ejecutando || enCurso}
              >
                Activar por el flujo normal
              </Button>
            )}
          </Box>

          {puedeAprovisionar && (
            <Typography variant="caption" color="text.secondary">
              Al aprovisionar, el cliente queda sin servicio entre el corte y la
              activación.
            </Typography>
          )}
        </Stack>
      </Paper>
    </Grid>
  );
}
